/**
 * @fileoverview Bulk edit tasks dialog (MOR-707)
 *
 * Opened from the task list's selection bar. Every field defaults to "leave
 * unchanged"; whatever the user sets is applied to all selected tasks via
 * `tasks.mutations.bulkUpdateTasks` (chunked, derived rows materialised
 * server-side). Mirrors the precedent library's bulk-edit flow.
 */

import { useState } from "react";
import { useConvexMutation } from "@convex-dev/react-query";
import { Loader2, Pencil } from "lucide-react";
import { toast } from "sonner";

import { api } from "@cvx/_generated/api";

import { CATEGORY_META, TASK_CATEGORY_ORDER } from "@/calendar/categoryMeta";
import { TASK_PRIORITIES } from "@/calendar/task-priority";
import type {
  BoardStatus,
  BoardLabel,
} from "@/calendar/components/board/task-board";
import type { BoardMember } from "@/calendar/components/board/task-board-card";
import { Button } from "@/ui/primitives/button";
import { Input } from "@/ui/primitives/input";
import { Label } from "@/ui/primitives/label";
import { Badge } from "@/ui/primitives/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import { SearchableSelect } from "@/ui/composed/inputs/searchable-select";
import { MultiSearchableSelect } from "@/ui/composed/inputs/multi-searchable-select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/composed/overlays/dialog";
import { getDisplayName } from "@/ui/composed";
import { extractErrorMessage } from "@/utils/errors";
import { cn } from "@/utils/misc";
import type { BulkTaskTarget } from "./bulk-edit-tasks-dialog.helpers";

const KEEP = "__keep__";
const UNASSIGN = "__unassign__";

export function BulkEditTasksDialog({
  targets,
  statuses,
  members,
  labels,
  open,
  onOpenChange,
  onApplied,
}: {
  targets: BulkTaskTarget[];
  statuses: BoardStatus[];
  members: BoardMember[];
  labels: BoardLabel[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after a successful apply so the host can clear the selection. */
  onApplied?: () => void;
}) {
  const bulkUpdate = useConvexMutation(api.tasks.mutations.bulkUpdateTasks);

  const [statusId, setStatusId] = useState<string>(KEEP);
  const [assignee, setAssignee] = useState<string>(KEEP);
  const [priority, setPriority] = useState<string>(KEEP);
  const [category, setCategory] = useState<string>(KEEP);
  const [labelIds, setLabelIds] = useState<string[]>([]);
  const [labelsTouched, setLabelsTouched] = useState(false);
  const [date, setDate] = useState("");
  const [plannedDate, setPlannedDate] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setStatusId(KEEP);
    setAssignee(KEEP);
    setPriority(KEEP);
    setCategory(KEEP);
    setLabelIds([]);
    setLabelsTouched(false);
    setDate("");
    setPlannedDate("");
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const submit = async () => {
    const patch: Record<string, unknown> = {};
    if (statusId !== KEEP) patch.statusId = statusId;
    if (assignee !== KEEP) {
      patch.assigneeUserId = assignee === UNASSIGN ? null : assignee;
    }
    if (priority !== KEEP) patch.priority = Number(priority);
    if (category !== KEEP) patch.category = category;
    if (labelsTouched) patch.labelIds = labelIds;
    if (date) patch.date = date;
    if (plannedDate) patch.plannedDate = plannedDate;

    if (Object.keys(patch).length === 0) {
      toast.error("Pick at least one field to change.");
      return;
    }

    setSubmitting(true);
    try {
      // The mutation caps at 50 targets per call — chunk the selection.
      const chunkStarts = Array.from(
        { length: Math.ceil(targets.length / 50) },
        (_, i) => i * 50,
      );
      const chunkResults: Awaited<ReturnType<typeof bulkUpdate>>[] = [];
      for (const start of chunkStarts) {
        chunkResults.push(
          await bulkUpdate({
            targets: targets.slice(start, start + 50),
            patch,
          }),
        );
      }
      const updated = chunkResults.reduce((sum, r) => sum + r.updated, 0);
      const failed = chunkResults.reduce((sum, r) => sum + r.failed, 0);
      const errors = chunkResults.flatMap((r) => r.errors);
      if (failed > 0) {
        toast.warning(
          `Updated ${updated} task${updated === 1 ? "" : "s"}; ${failed} failed. ${errors[0] ?? ""}`,
        );
      } else {
        toast.success(`Updated ${updated} task${updated === 1 ? "" : "s"}.`);
      }
      reset();
      onOpenChange(false);
      onApplied?.();
    } catch (e) {
      toast.error(extractErrorMessage(e, "Could not update the tasks."));
    } finally {
      setSubmitting(false);
    }
  };

  const memberOptions = [
    { value: UNASSIGN, label: "Unassign" },
    ...members.map((m) => ({
      value: m._id,
      label: getDisplayName(m.firstName, m.lastName, m.email),
    })),
  ];

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            Edit {targets.length} task{targets.length === 1 ? "" : "s"}
          </DialogTitle>
          <DialogDescription>
            Only the fields you set are changed; everything else keeps its
            current value on each task.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs font-medium text-muted-foreground">
                Status
              </Label>
              <Select value={statusId} onValueChange={setStatusId}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={KEEP}>Keep current</SelectItem>
                  {statuses.map((s) => (
                    <SelectItem key={String(s._id)} value={String(s._id)}>
                      <span className="flex items-center gap-2">
                        <Badge color={s.color} variant="soft" size="sm">
                          {s.name}
                        </Badge>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label className="text-xs font-medium text-muted-foreground">
                Assignee
              </Label>
              <SearchableSelect
                value={assignee}
                onValueChange={setAssignee}
                options={memberOptions}
                allOptionLabel="Keep current"
                allOptionValue={KEEP}
                placeholder="Keep current"
                searchPlaceholder="Search members…"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs font-medium text-muted-foreground">
                Priority
              </Label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={KEEP}>Keep current</SelectItem>
                  {TASK_PRIORITIES.map((p) => {
                    const Icon = p.icon;
                    return (
                      <SelectItem key={p.value} value={String(p.value)}>
                        <span className="flex items-center gap-2">
                          <Icon className="size-3.5" />
                          {p.label}
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label className="text-xs font-medium text-muted-foreground">
                Category
              </Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={KEEP}>Keep current</SelectItem>
                  {TASK_CATEGORY_ORDER.map((c) => {
                    const meta = CATEGORY_META[c];
                    const Icon = meta.icon;
                    return (
                      <SelectItem key={c} value={c}>
                        <span className="flex items-center gap-2">
                          <Icon className={cn("size-3.5", meta.iconFgClass)} />
                          {meta.label}
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label className="text-xs font-medium text-muted-foreground">
              Labels (replaces the set on each task)
            </Label>
            <MultiSearchableSelect
              value={labelIds}
              onValueChange={(ids) => {
                setLabelIds(ids);
                setLabelsTouched(true);
              }}
              options={labels.map((l) => ({
                value: String(l._id),
                label: l.name,
              }))}
              placeholder={labelsTouched ? "No labels" : "Keep current"}
              searchPlaceholder="Search labels…"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs font-medium text-muted-foreground">
                Due date
              </Label>
              <Input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs font-medium text-muted-foreground">
                Planned date
              </Label>
              <Input
                type="date"
                value={plannedDate}
                onChange={(e) => setPlannedDate(e.target.value)}
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => handleOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button onClick={submit} disabled={submitting} className="gap-1.5">
            {submitting ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Pencil className="size-3.5" />
            )}
            Apply to {targets.length}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
